// Nutzungsbalken für Stellen, Zugänge und Talentspeicher. Grün im Rahmen,
// amber ab 80 %, rot ab voll. Die Zustände kommen aus LIMIT_STATES der
// Spiegel-Logik, damit OS und Dashboard dieselben Schwellen zeigen.
import { classNames } from '../../helper'
import { formatCount, isUnlimited } from '../../helper/pricing'
import {
  LIMIT_STATES,
  resolveLimitState
} from '../../helper/pricing-mirror/limits'

const FILL = {
  [LIMIT_STATES.OK]: 'bg-green-500',
  [LIMIT_STATES.WARNING]: 'bg-amber-400',
  [LIMIT_STATES.AT_LIMIT]: 'bg-red-500',
  [LIMIT_STATES.OVER_LIMIT]: 'bg-red-500'
}

const STATE_TEXT = {
  [LIMIT_STATES.WARNING]: {
    text: 'Ab 80 % der Grenze',
    className: 'text-amber-700'
  },
  [LIMIT_STATES.AT_LIMIT]: {
    text: 'Grenze erreicht',
    className: 'text-red-700'
  },
  [LIMIT_STATES.OVER_LIMIT]: {
    text: 'Über der Grenze',
    className: 'text-red-700'
  }
}

// Gespeicherte Mengen ab UNLIMITED gelten als unbegrenzt, 0 und leer als
// unbekannt. Aufrufer mit Plan-Payment geben am besten effectiveLimitsOf
// aus src/helper/pricing.js weiter.
const toLimit = (limit) => {
  if (limit === null || limit === undefined || limit === '') return null
  if (isUnlimited(limit)) return Infinity
  const n = Number(limit)
  return Number.isNaN(n) || n === 0 ? null : n
}

// Gesperrt heißt heute gesperrt. Ohne aktive Sperre nur „wären gesperrt".
export const lockedTalentsText = (count, { lockPending = false } = {}) => {
  const talents = count === 1 ? '1 Talent' : `${formatCount(count)} Talente`
  return lockPending
    ? `${talents} wären mit aktiver Sperre gesperrt`
    : `${talents} gesperrt`
}

/**
 * Zustand und Füllung, rein und ohne React. Gezeichnet wird nur bei
 * endlichem Limit und bekannter Nutzung. Über dem Limit bleibt die Füllung
 * bei 100 %.
 */
export const usageMeterModel = ({ used = null, limit = null } = {}) => {
  const resolved = resolveLimitState({
    resource: null,
    limit: toLimit(limit),
    used
  })
  const isMeter = Object.prototype.hasOwnProperty.call(FILL, resolved.state)

  let percent = 0
  if (isMeter) {
    percent =
      resolved.limit > 0
        ? Math.min(100, (resolved.used / resolved.limit) * 100)
        : 100
  }

  return {
    state: resolved.state,
    used: resolved.used,
    limit: resolved.limit,
    percent,
    fill: FILL[resolved.state] || null,
    isMeter
  }
}

const valueTextOf = (model, { failed }) => {
  if (failed && model.used === null) return 'Nutzung nicht geladen'
  if (model.isMeter) {
    return `${formatCount(model.used)} von ${formatCount(model.limit)}`
  }
  if (model.state === LIMIT_STATES.UNLIMITED) {
    return model.used === null
      ? 'Unbegrenzt'
      : `${formatCount(model.used)}, unbegrenzt`
  }
  if (model.state === LIMIT_STATES.PENDING) return 'Wird gezählt'
  return model.used === null
    ? 'Kein Limit hinterlegt'
    : `${formatCount(model.used)}, kein Limit hinterlegt`
}

/**
 * @param {object} props
 * @param {string} props.label z. B. „Gleichzeitig offene Stellen"
 * @param {number|null} props.used belegte Menge, null heißt noch nicht gezählt
 * @param {number|null} props.limit Limit, ab UNLIMITED oder Infinity unbegrenzt
 * @param {number} [props.lockedTalents] gesperrte Personen im Talentspeicher
 * @param {boolean} [props.lockPending] die Sperre greift für diesen Kunden
 *   noch nicht, lockedTalents wären erst mit ihr gesperrt
 * @param {boolean} [props.failed] Zählen ist fehlgeschlagen
 * @param {string} [props.hint] kleine Zeile unter dem Balken
 */
const UsageMeter = ({
  label,
  used = null,
  limit = null,
  lockedTalents = 0,
  lockPending = false,
  failed = false,
  hint,
  className
}) => {
  const model = usageMeterModel({ used, limit })
  const valueText = valueTextOf(model, { failed })
  const stateText = STATE_TEXT[model.state]

  const meterProps = model.isMeter
    ? {
        role: 'meter',
        'aria-label': label,
        'aria-valuemin': 0,
        'aria-valuemax': model.limit,
        'aria-valuenow': Math.min(model.used, model.limit),
        'aria-valuetext': `${label}, ${valueText}`
      }
    : {}

  return (
    <div className={className}>
      <div className='flex items-baseline justify-between gap-x-4 text-sm'>
        <span className='font-medium text-gray-900'>{label}</span>
        <span className='ml-auto whitespace-nowrap tabular-nums text-gray-700'>
          {valueText}
        </span>
      </div>
      <div
        {...meterProps}
        data-state={model.state}
        className={classNames(
          'mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200',
          model.state === LIMIT_STATES.PENDING && !failed && 'animate-pulse'
        )}
      >
        {model.isMeter && (
          <div
            className={classNames('h-full rounded-full', model.fill)}
            style={{ width: `${model.percent}%` }}
          />
        )}
      </div>
      {stateText && (
        <p
          className={classNames(
            'mt-1.5 text-xs font-medium',
            stateText.className
          )}
        >
          {stateText.text}
        </p>
      )}
      {lockedTalents > 0 && (
        <p
          className={classNames(
            'mt-1 text-xs font-medium',
            lockPending ? 'text-amber-700' : 'text-red-700'
          )}
        >
          {lockedTalentsText(lockedTalents, { lockPending })}
        </p>
      )}
      {hint && <p className='mt-1 text-xs text-gray-500'>{hint}</p>}
    </div>
  )
}

export default UsageMeter
